import type { ConsentState } from './types';

const STORAGE_VERSION = '1.0';

export function storageKey(tid: string | null): string {
  return `emerge-privacy-consent-${tid ?? 'default'}`;
}

export function isConsentStateFresh(state: ConsentState, now: Date): boolean {
  const timestamp = new Date(state.timestamp);
  if (Number.isNaN(timestamp.getTime()) || Number.isNaN(now.getTime())) return false;
  if (timestamp.getTime() > now.getTime()) return false;

  timestamp.setUTCMonth(timestamp.getUTCMonth() + 6);
  return now.getTime() < timestamp.getTime();
}

function removeState(tid: string | null): void {
  try {
    localStorage.removeItem(storageKey(tid));
  } catch {
    // localStorage può essere disabilitato o non scrivibile; non fail
  }
}

export function readState(tid: string | null, now = new Date()): ConsentState | null {
  try {
    const raw = localStorage.getItem(storageKey(tid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed || typeof parsed !== 'object') {
      removeState(tid);
      return null;
    }
    if (parsed.version !== STORAGE_VERSION) {
      // futura migrazione possibile; per ora ignoriamo dati vecchi
      removeState(tid);
      return null;
    }
    if (!parsed.categories || parsed.categories.necessary !== true
        || Object.values(parsed.categories).some((value) => typeof value !== 'boolean')) {
      removeState(tid);
      return null;
    }
    if (!isConsentStateFresh(parsed, now)) {
      removeState(tid);
      return null;
    }
    return parsed;
  } catch {
    removeState(tid);
    return null;
  }
}

export function writeState(tid: string | null, state: ConsentState): void {
  try {
    localStorage.setItem(storageKey(tid), JSON.stringify(state));
  } catch {
    // localStorage può essere disabilitato o pieno; non fail
  }
}

export function buildState(
  categories: ConsentState['categories'],
): ConsentState {
  // necessary è sempre true per definizione (cookie tecnici essenziali)
  const safeCategories: ConsentState['categories'] = {
    ...Object.fromEntries(Object.entries(categories).map(([category, value]) => [category, value === true])),
    necessary: true,
  };
  return {
    categories: safeCategories,
    timestamp: new Date().toISOString(),
    version: STORAGE_VERSION,
  };
}
